import React from 'react';
import { Box, Typography, List, ListItem, useTheme } from '@mui/material';
import { useInView } from 'react-intersection-observer';

const ServiceCard = ({ icon, title, items, index }) => {
  const theme = useTheme();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1, // Card becomes visible when 10% of it is in view
  });

  return (
    <Box
      ref={ref}
      sx={{
        // --- Base State ---
        width: '300px',
        maxWidth: 350,
        minHeight: 380, // Set a min-height for consistency
        p: 4,
        borderRadius: 2,
        boxShadow: 3,
        backgroundColor: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        borderTop: `4px solid transparent`, // Placeholder for hover effect

        // --- Animation Properties ---
        opacity: 0,
        transform: 'translateY(50px)', // Start 50px lower
        transition: 'all 0.5s ease-in-out',
        transitionDelay: `${index * 0.1}s`, // Staggered delay

        // --- In-View (Visible) State ---
        ...(inView && {
          opacity: 1,
          transform: 'translateY(0)',
        }),

        // --- Hover State ---
        '&:hover': {
          boxShadow: 8,
          transform: 'translateY(-10px)', // Lifts the card up more
          borderTop: `4px solid ${theme.palette.primary.main}`,
          
          // Animate icon on hover
          '& .service-icon': {
            transform: 'scale(1.2)',
            color: theme.palette.primary.main,
          },
        },
      }}
    >
      {/* Icon */}
      <Box 
        className="service-icon"
        sx={{ 
          mb: 2,
          color: theme.palette.text.secondary,
          transition: 'transform 0.3s ease-in-out, color 0.3s ease-in-out',
          textAlign: 'center', // Center the icon
        }}
      >
        {icon}
      </Box>

      {/* Title */}
      <Typography 
        variant="h6" 
        component="h3" 
        gutterBottom
        sx={{ 
          fontWeight: 'bold',
          textAlign: 'center', // Center the title
          minHeight: '3.5rem', // Consistent height for title area
        }}
      >
        {title}
      </Typography>

      {/* List of Items */}
      <List dense sx={{ flex: 1, overflow: 'auto' }}>
        {items.map((item, idx) => (
          <ListItem key={idx} sx={{ py: 0.2, pl: 0 }}>
            <Typography variant="body2" component="span">
              • {item}
            </Typography>
          </ListItem>
        ))}
      </List>
    </Box>
  );
};

export default ServiceCard;